import type { HttpError } from '@refinedev/core'
import { useShow } from '@refinedev/core'
import type { Game } from '@sirius/contracts/admin'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { ShowView, ShowViewHeader } from '~/components/refineUi/views/showView'
import { formatDateTime } from '~/i18n/format'
import { History } from '~/pages/activity/history'
import { NotFoundPage } from '~/pages/notFound'
import { GameConfigurationList } from './configurationList'
import { configurationPath } from './paths'

export function GameShow() {
  const { t } = useTranslation()
  const { query } = useShow<Game, HttpError>({ resource: 'games' })
  const game = query.data?.data

  if (query.error?.statusCode === 404) {
    return <NotFoundPage />
  }

  return (
    <ShowView>
      {/* a game comes with a release; staff edit its configurations */}
      <ShowViewHeader
        title={game?.name}
        canEdit={false}
      />
      {query.error && (
        <p
          role="alert"
          className="text-destructive text-sm"
        >
          {t('gamesPage.show.loadError', { message: query.error.message })}
        </p>
      )}
      {game && (
        <div className="space-y-8">
          <dl className="grid max-w-md grid-cols-[10rem_1fr] gap-2 text-sm">
            <dt className="text-muted-foreground">
              {t('gamesPage.show.fields.name')}
            </dt>
            <dd>{game.name}</dd>
            <dt className="text-muted-foreground">
              {t('gamesPage.show.fields.slug')}
            </dt>
            <dd>{game.slug}</dd>
            <dt className="text-muted-foreground">
              {t('gamesPage.show.fields.kind')}
            </dt>
            <dd>{game.kind}</dd>
            <dt className="text-muted-foreground">
              {t('gamesPage.show.fields.defaultConfiguration')}
            </dt>
            <dd>
              <Link
                to={configurationPath(game.id, game.defaultConfigurationId)}
                className="hover:text-primary underline underline-offset-4"
              >
                {game.defaultConfigurationName}
              </Link>{' '}
              <span className="text-muted-foreground tabular-nums">
                {t('gamesPage.show.defaultVersion', {
                  version: game.configVersion,
                })}
              </span>
            </dd>
            <dt className="text-muted-foreground">
              {t('gamesPage.show.fields.created')}
            </dt>
            <dd>{formatDateTime(game.createdAt)}</dd>
            <dt className="text-muted-foreground">
              {t('gamesPage.show.fields.updated')}
            </dt>
            <dd>{formatDateTime(game.updatedAt)}</dd>
          </dl>
          <section
            aria-labelledby="configurations"
            className="space-y-3"
          >
            <div className="space-y-1">
              <h3
                id="configurations"
                className="font-semibold"
              >
                {t('gamesPage.show.configurations.heading')}
              </h3>
              <p className="text-muted-foreground max-w-2xl text-sm">
                {t('gamesPage.show.configurations.intro')}
              </p>
            </div>
            <GameConfigurationList game={game} />
          </section>
          {/* configuration saves and default changes: staff-only platform
              events */}
          <History record={{ entity: 'game', id: game.id, tenantId: null }} />
        </div>
      )}
    </ShowView>
  )
}
